import { Card, CardContent } from "components/ui/card";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { PolicyOption, countPlacesByStreet, curbZoneCounter } from "./onstreet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "components/ui/table";
import { disponibilite, perPages } from "./onstreet.const";
import { useEffect, useMemo, useState } from "react";

import { Combobox } from "@/components/form-control";
import { CurbZone } from "@/types/curb-zone.type";
import { Input } from "components/ui/input";
import { OnStreetSkeletonView } from "./OnStreetSkeletonView";
import { PaginationControls } from "./PaginationControls";
import { Search } from "lucide-react";
import { SortableHeader } from "./SortableHeader";
import { TableSkeleton } from "@/components/data-table";
import { TotalAreaDisplay } from "./TotalAreaDisplay";
import { convertColor } from "helper/utils";
import { numberFormat } from "helper/format-data";

type StreetCountData = {
  name: string;
  count: number;
};

type StreetStatisticsViewProps = {
  groupedData: Map<string, CurbZone[]>;
  isLoadingCurbZones: boolean;
  listPolicy: PolicyOption[];
};

export const PoliciesStatisticsView = ({ groupedData, isLoadingCurbZones, listPolicy }: StreetStatisticsViewProps) => {
  const [currentPolicy, setCurrentPolicy] = useState<PolicyOption>();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  useEffect(() => {
    setCurrentPolicy(listPolicy[0]);
  }, [listPolicy]);

  const selectedCurbZone = useMemo(() => {
    if (currentPolicy) {
      return groupedData.get(currentPolicy.value) ?? [];
    }
    return [];
  }, [currentPolicy, groupedData]);

  const placeCounting = useMemo(() => countPlacesByStreet(selectedCurbZone), [selectedCurbZone]);
  const curbZoneSummary = useMemo(() => curbZoneCounter(selectedCurbZone), [selectedCurbZone]);

  // Define columns with sorting functionality
  const columns: ColumnDef<StreetCountData>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => <SortableHeader column={column}>{disponibilite.policies[0]}</SortableHeader>,
      cell: ({ row }) => <div className="font-medium">{row.getValue("name")}</div>,
    },
    {
      accessorKey: "count",
      header: ({ column }) => (
        <SortableHeader column={column} className="text-right">
          {disponibilite.policies[1]}
        </SortableHeader>
      ),
      cell: ({ row }) => <div className="text-right">{row.getValue("count")}</div>,
    },
  ];

  const onSelectPolicy = (value: string) => {
    const policy = listPolicy?.find((item) => item.value === value);
    setCurrentPolicy(policy);
  };

  const table = useReactTable({
    data: placeCounting,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: perPages[0],
      },
    },
    state: {
      sorting,
      columnFilters,
    },
  });

  const handleSearch = (input: string) => {
    table.getColumn("name")?.setFilterValue(input);
  };

  return (
    <div className="flex">
      {isLoadingCurbZones ? (
        <OnStreetSkeletonView />
      ) : (
        <>
          <div className="flex flex-col mr-14 w-96 gap-4">
            <Combobox
              value={currentPolicy?.value}
              options={listPolicy}
              onValueChange={(value) => onSelectPolicy(value)}
              placeholder="Search policy..."
              className="w-full"
            />
            <div className="rounded-xl border p-5">
              <div className="flex items-center">
                <div className="mr-1.5 w-2.5 h-2.5">
                  <div
                    style={{ backgroundColor: currentPolicy?.lineColor ? convertColor(currentPolicy?.lineColor) : "" }}
                    className="size-2.5 rounded-full mr-1.5"
                  />
                </div>
                <span className="font-bold text-xl">{currentPolicy?.label}</span>
              </div>
              <span className="text-13px mt-2">Type</span>
            </div>
            <div className="rounded-xl border p-5">
              <span className="text-3xl font-bold">{numberFormat(curbZoneSummary.totalPlace)}</span>
              <span className="text-13px mt-2">&nbsp;Places</span>
            </div>
            <div className="rounded-xl border p-5 flex flex-wrap items-end">
              <TotalAreaDisplay totalArea={curbZoneSummary.totalArea} />
              &nbsp;
              <span className="text-13px mt-2">Superficie</span>
            </div>
          </div>
          <div className="grow">
            <Card>
              <CardContent>
                <div className="flex items-center py-4">
                  <div className="relative max-w-sm">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Filtrer par nom de voies..."
                      onChange={(event) => handleSearch(event.target.value)}
                      className="pl-8 w-60"
                    />
                  </div>
                </div>
                <div className="w-full rounded-md border max-h-[calc(100vh-20rem)] overflow-y-auto">
                  <Table className="m-auto border-collapse relative">
                    <TableHeader>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                          {headerGroup.headers.map((header) => {
                            return (
                              <TableHead
                                key={header.id}
                                className="sticky top-0 bg-white border-b-2 border-secondary z-10"
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                              </TableHead>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableHeader>
                    {isLoadingCurbZones ? (
                      <TableBody>
                        <TableSkeleton rows={5} colSpan={2} />
                      </TableBody>
                    ) : (
                      <TableBody className="text-table-body text-medium">
                        {table.getRowModel().rows?.length ? (
                          table.getRowModel().rows.map((row, idx) => (
                            <TableRow
                              key={row.id}
                              data-state={row.getIsSelected() && "selected"}
                              className={idx % 2 === 1 ? "bg-muted/50" : ""}
                            >
                              {row.getVisibleCells().map((cell) => (
                                <TableCell key={cell.id}>
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={columns.length} className="h-24 text-center">
                              No results.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    )}
                  </Table>
                </div>
                <PaginationControls table={table} />
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};
